import api from "./api";
import { isoDateTimeWithDashes } from "../utils/DateTime";
import Logger from "../utils/Logger";
import ExportResultQueryParams from "@/interfaces/ExportResultQueryParams";

const EXPORT_REQUEST_TIMEOUT_MS = 120000;

/**
 * Extracts the filename from the Content-Disposition header.
 * @param contentDisposition - The Content-Disposition header value.
 * @returns The extracted filename or null if not found.
 */
const getFilenameFromContentDisposition = (
  contentDisposition: string,
): string | null => {
  if (!contentDisposition) {
    Logger.debug("No Content-Disposition header found", ["ExportApi"]);
    return null;
  }

  let filename: string | null = null;

  // First, try to extract filename* (UTF-8 encoded)
  const utf8FilenameRegex = /filename\*=UTF-8''([\w%\-\.]+)((?:; ?)|$)/i;
  const utf8Match = contentDisposition.match(utf8FilenameRegex);

  if (utf8Match && utf8Match[1]) {
    filename = decodeURIComponent(utf8Match[1]);
    Logger.debug("Extracted UTF-8 filename from Content-Disposition", [
      "ExportApi",
    ]);
  } else {
    // If filename* is not found, try to extract filename
    const filenameStart = contentDisposition.toLowerCase().indexOf("filename=");
    if (filenameStart >= 0) {
      const partialDisposition = contentDisposition.slice(filenameStart);
      const asciiFilenameRegex = /^filename=(["']?)(.*?)(?:; ?|$)/i;
      const asciiMatch = partialDisposition.match(asciiFilenameRegex);

      if (asciiMatch && asciiMatch[2]) {
        // Remove quotes if present
        filename = asciiMatch[2].replace(/^["']|["']$/g, "");
        Logger.debug("Extracted ASCII filename from Content-Disposition", [
          "ExportApi",
        ]);
      }
    }
  }

  if (!filename) {
    Logger.warn("Could not extract filename from Content-Disposition header", [
      "ExportApi",
    ]);
  }

  return filename;
};

/**
 * Fetches the CSV from the `/export` endpoint (GET).
 * @returns A Blob containing the CSV data and the extracted filename.
 */
const fetchExportCSV = async (): Promise<{ data: Blob; filename: string }> => {
  try {
    Logger.info("Starting CSV export API request", ["ExportApi"]);

    const response = await api
      .get("/export", {
        responseType: "blob",
      })
      .then((res) => {
        Logger.info("CSV export API request completed successfully", [
          "ExportApi",
        ]);
        return res;
      })
      .catch((err) => {
        Logger.error("Error in CSV export API call", err, ["ExportApi"]);
        throw err;
      });

    const contentDisposition: string =
      response.headers["content-disposition"] || "";

    const filename =
      getFilenameFromContentDisposition(contentDisposition) ||
      `scrm-export-${isoDateTimeWithDashes}.csv`;

    Logger.info("CSV export completed with filename", ["ExportApi"], {
      filename,
      hasContentDisposition: !!contentDisposition,
    });

    return {
      data: response.data,
      filename,
    };
  } catch (error) {
    Logger.error("Error fetching export CSV", error, ["ExportApi"]);
    throw error;
  }
};

/**
 * Fetches the CSV from the `/export` endpoint (POST),
 * using provided search parameters in a POST request body.
 * @param exportResultSearchParams - The filter parameters to be sent in the request body.
 * @returns A Blob containing the CSV data and the extracted filename.
 */
const fetchExportResultsCSV = async (
  exportResultSearchParams: ExportResultQueryParams,
): Promise<{ data: Blob; filename: string }> => {
  try {
    Logger.info(
      "Export results request with search filters",
      ["ExportResultsCSV"],
      { searchParams: exportResultSearchParams as Record<string, unknown> },
    );

    const response = await api
      .post("/export", exportResultSearchParams, {
        responseType: "blob",
        timeout: EXPORT_REQUEST_TIMEOUT_MS,
      })
      .then((res) => {
        Logger.info("Export results api request completed successfully", [
          "ExportResultsCSV",
        ]);
        return res;
      })
      .catch((err) => {
        Logger.error("Error in export results api call", err, [
          "ExportResultsCSV",
        ]);
        throw err;
      });

    const contentDisposition: string =
      response.headers["content-disposition"] || "";
    const filename =
      getFilenameFromContentDisposition(contentDisposition) ||
      `scrm-export-${isoDateTimeWithDashes}.csv`;
    Logger.info(
      "Export results CSV with search filters",
      ["ExportResultsCSV"],
      { filename, hasContentDisposition: !!contentDisposition },
    );

    return {
      data: response.data,
      filename,
    };
  } catch (error) {
    Logger.error("Error fetching export results", error, ["ExportResultsCSV"]);
    throw error;
  }
};

export const ExportApi = {
  fetchExportCSV,
  fetchExportResultsCSV,
};
